<script lang="ts">
    import { createEventDispatcher } from 'svelte';
    import { auth, AUTH_LEVELS } from '../stores/auth';
    import type { AuthLevel, LevelDef } from '../stores/auth';
    import Modal from './Modal.svelte';
    import Button from './forms/Button.svelte';
    import PinInput from './forms/PinInput.svelte';
    import Icon from './Icon.svelte';

    export let open = false;

    const dispatch = createEventDispatcher<{ authenticated: AuthLevel }>();

    type Step = 'checking' | 'select-level' | 'usb-connect' | 'pin-entry' | 'success';

    let step: Step = 'checking';
    let selectedLevel: LevelDef | null = null;
    let pinErrors: Record<string, string[]> = { pin: [] };
    let pinValue = '';
    let usbDetected = false;
    let usbTimer: ReturnType<typeof setTimeout>;
    let modalInitialized = false;

    // Trigger init only once per open, without depending on auth store (avoids re-trigger loop)
    $: if (open && !modalInitialized) {
        modalInitialized = true;
        step = 'checking';
        selectedLevel = null;
        pinErrors = { pin: [] };
        pinValue = '';
        usbDetected = false;
        setTimeout(() => { step = 'select-level'; }, 600);
    }
    $: if (!open) modalInitialized = false;

    function selectLevel(level: LevelDef) {
        selectedLevel = level;
        pinErrors = { pin: [] };
        pinValue = '';
        if (level.requiresUsb) {
            step = 'usb-connect';
            // Auto-simulate USB connection after 4 seconds
            clearTimeout(usbTimer);
            usbDetected = false;
            usbTimer = setTimeout(() => {
                usbDetected = true;
                auth.setUsbConnected(true);
                setTimeout(() => { step = 'pin-entry'; }, 800);
            }, 4000);
        } else {
            step = 'pin-entry';
        }
    }

    let pinInputRef: { reset: () => void; focusFirst: () => void } | undefined;

    function submitPin() {
        if (!selectedLevel) return;
        if (pinValue === selectedLevel.pin) {
            auth.login(selectedLevel.id);
            step = 'success';
            setTimeout(() => {
                open = false;
                dispatch('authenticated', selectedLevel!.id);
            }, 1200);
        } else {
            pinErrors = { pin: ['Incorrect PIN. Please try again.'] };
            pinValue = '';
            pinInputRef?.reset();
        }
    }

    function goBack() {
        step = 'select-level';
        selectedLevel = null;
    }

    const LEVEL_ICONS: Record<AuthLevel, string> = {
        maintenance: 'M14.7 6.3a1 1 0 0 0 0 1.4l1.6 1.6a1 1 0 0 0 1.4 0l3.77-3.77a6 6 0 0 1-7.94 7.94l-6.91 6.91a2.12 2.12 0 0 1-3-3l6.91-6.91a6 6 0 0 1 7.94-7.94l-3.76 3.76z',
        technician:  'M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18',
        admin:       'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
    };
</script>

<Modal bind:open title="Authentication" size="sm">

    <!-- Step: Checking -->
    {#if step === 'checking'}
        <div class="auth-body auth-body--center">
            <div class="auth-spinner" aria-label="Checking session..."></div>
            <p class="auth-hint">Checking session…</p>
        </div>

    <!-- Step: Select level -->
    {:else if step === 'select-level'}
        <div class="auth-body">
            <p class="auth-label">Select your access level</p>
            <div class="level-list">
                {#each AUTH_LEVELS as lvl}
                    <button class="level-card" on:click={() => selectLevel(lvl)}>
                        <svg class="level-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                             stroke-width="1.75" stroke-linecap="round" stroke-linejoin="round"
                             width="22" height="22" aria-hidden="true">
                            <path d={LEVEL_ICONS[lvl.id]} />
                        </svg>
                        <div class="level-info">
                            <span class="level-name">{lvl.label}</span>
                            <span class="level-req">{lvl.requiresUsb ? 'USB key + PIN' : 'PIN only'}</span>
                        </div>
                        <svg class="level-chevron" viewBox="0 0 16 16" fill="none" stroke="currentColor"
                             stroke-width="2" stroke-linecap="round" width="14" height="14" aria-hidden="true">
                            <path d="M6 3l5 5-5 5"/>
                        </svg>
                    </button>
                {/each}
            </div>
        </div>

    <!-- Step: USB connect -->
    {:else if step === 'usb-connect'}
        <div class="auth-body auth-body--center">
            <div class="usb-graphic" class:usb-graphic--detected={usbDetected}>
                {#if usbDetected}
                    <svg class="usb-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="52" height="52" aria-hidden="true">
                        <path d="M8 13C9.66 13 11 14.34 11 16C11 17.66 9.66 19 8 19C6.34 19 5 17.66 5 16C5 14.34 6.34 13 8 13M8 15C7.45 15 7 15.45 7 16C7 16.55 7.45 17 8 17C8.55 17 9 16.55 9 16C9 15.45 8.55 15 8 15M9.77 4.33L10.5 5.08L14.29 1.29C14.47 1.11 14.72 1 15 1C15.28 1 15.53 1.11 15.71 1.29L22.78 8.36L22.78 8.37C22.92 8.54 23 8.76 23 9C23 9.3 22.87 9.57 22.66 9.76L22.66 9.76L18.93 13.5L19.67 14.23L12.95 20.95C11.68 22.22 9.93 23 8 23C4.13 23 1 19.87 1 16C1 14.07 1.78 12.32 3.05 11.05L9.77 4.33M11.54 19.54L16.84 14.23L9.77 7.16L4.46 12.46C3.56 13.37 3 14.62 3 16C3 18.76 5.24 21 8 21C9.38 21 10.63 20.44 11.54 19.54M15.07 4.69L16.5 6.1L15.07 7.5L13.66 6.1L15.07 4.69M17.9 7.5L19.31 8.93L17.9 10.34L16.5 8.93L17.9 7.5M20.59 9L15 3.41L11.93 6.5L17.5 12.08L20.59 9Z" />
                    </svg>
                {:else}
                    <svg class="usb-icon usb-insert" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="52" height="52" aria-hidden="true">
                        <path d="M8 13C9.66 13 11 14.34 11 16C11 17.66 9.66 19 8 19C6.34 19 5 17.66 5 16C5 14.34 6.34 13 8 13M8 15C7.45 15 7 15.45 7 16C7 16.55 7.45 17 8 17C8.55 17 9 16.55 9 16C9 15.45 8.55 15 8 15M9.77 4.33L10.5 5.08L14.29 1.29C14.47 1.11 14.72 1 15 1C15.28 1 15.53 1.11 15.71 1.29L22.78 8.36L22.78 8.37C22.92 8.54 23 8.76 23 9C23 9.3 22.87 9.57 22.66 9.76L22.66 9.76L18.93 13.5L19.67 14.23L12.95 20.95C11.68 22.22 9.93 23 8 23C4.13 23 1 19.87 1 16C1 14.07 1.78 12.32 3.05 11.05L9.77 4.33M11.54 19.54L16.84 14.23L9.77 7.16L4.46 12.46C3.56 13.37 3 14.62 3 16C3 18.76 5.24 21 8 21C9.38 21 10.63 20.44 11.54 19.54M15.07 4.69L16.5 6.1L15.07 7.5L13.66 6.1L15.07 4.69M17.9 7.5L19.31 8.93L17.9 10.34L16.5 8.93L17.9 7.5M20.59 9L15 3.41L11.93 6.5L17.5 12.08L20.59 9Z" />
                    </svg>
                {/if}
            </div>
            {#if usbDetected}
                <p class="auth-hint auth-hint--success">USB key detected — preparing…</p>
            {:else}
                <p class="auth-hint">Connect your authentication USB key</p>
                <p class="auth-hint usb-countdown">Detecting automatically…</p>
            {/if}
            <Button variant="default" mode="outlined" on:click={goBack}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M8 2L3 7l5 5"/>
                </svg>
                Back
            </Button>
        </div>

    <!-- Step: PIN entry -->
    {:else if step === 'pin-entry'}
        <div class="auth-body auth-body--center">
            <div class="pin-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5"
                     stroke-linecap="round" stroke-linejoin="round" width="36" height="36" aria-hidden="true">
                    <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                    <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                </svg>
            </div>
            <p class="auth-label">Enter PIN for <strong>{selectedLevel?.label}</strong></p>
            {#key step}
                <PinInput bind:this={pinInputRef} name="pin" errors={pinErrors} length={4} bind:value={pinValue} autofocus onEnter={submitPin} />
            {/key}
            <Button variant="primary" on:click={submitPin} disabled={pinValue.length < 4}>
                Confirm
            </Button>
            <Button variant="default" mode="text" on:click={goBack}>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none" stroke="currentColor"
                     stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">
                    <path d="M8 2L3 7l5 5"/>
                </svg>
                Back
            </Button>
        </div>

    <!-- Step: Success -->
    {:else if step === 'success'}
        <div class="auth-body auth-body--center">
            <div class="success-icon">
                <Icon name="success" size={48} />
            </div>
            <p class="auth-hint auth-hint--success">
                Logged in as <strong>{selectedLevel?.label}</strong>
            </p>
        </div>
    {/if}

</Modal>

<style lang="scss">
    .auth-body {
        display: flex;
        flex-direction: column;
        gap: 0.75rem;

        &--center {
            align-items: center;
            text-align: center;
        }
    }

    .auth-label {
        font-size: 0.875rem;
        color: $color-text-secondary;
        margin: 0;
    }

    .auth-hint {
        font-size: 0.85rem;
        color: $color-text-muted;
        margin: 0;

        &--success {
            color: $color-success;
            font-weight: 500;
        }
    }

    // ── Level cards ───────────────────────────────────────────────────────────

    .level-list {
        display: flex;
        flex-direction: column;
        gap: 0.5rem;
    }

    .level-card {
        display: flex;
        align-items: center;
        gap: 0.875rem;
        padding: 0.875rem 1rem;
        background: $color-bg-offset; // Use offset color instead of border
        border: none; // No border
        border-radius: 12px; // Rounded corners
        cursor: pointer;
        text-align: left;
        color: $color-text;
        width: 100%;
        transition: background-color 0.15s ease;

        &:hover {
            background-color: $color-bg-active;
            // No border color change needed
        }
    }

    .level-icon {
        flex-shrink: 0;
        color: $color-primary;
    }

    .level-info {
        flex: 1;
        display: flex;
        flex-direction: column;
        gap: 0.1rem;
    }

    .level-name {
        font-size: 0.9rem;
        font-weight: 500;
    }

    .level-req {
        font-size: 0.75rem;
        color: $color-text-muted;
    }

    .level-chevron {
        color: $color-text-muted;
        flex-shrink: 0;
    }

    // ── USB graphic ───────────────────────────────────────────────────────────

    .usb-graphic {
        width: 88px;
        height: 88px;
        display: flex;
        align-items: center;
        justify-content: center;
        color: $color-text-muted;
        transition: border-color 0.4s ease, color 0.4s ease;

        &--detected {
            color: $color-success;
        }
    }

    .usb-icon {
        fill: currentColor;
    }

    .usb-insert {
        animation: usb-insert 1.4s ease-in-out infinite;
        transform-origin: center;
    }

    @keyframes usb-insert {
        0%   { transform: translate(5px, 0px);  opacity: 0.4; }
        50%  { transform: translate(0px, 5px);  opacity: 1; }
        100% { transform: translate(5px, 0px);  opacity: 0.4; }
    }

    .usb-countdown {
        font-size: 0.75rem;
        font-style: italic;
    }

    // ── PIN entry ─────────────────────────────────────────────────────────────

    .pin-icon {
        color: $color-primary;
    }

    // ── Success ───────────────────────────────────────────────────────────────

    .success-icon {
        color: $color-success;
    }

    // ── Spinner ───────────────────────────────────────────────────────────────

    .auth-spinner {
        width: 36px;
        height: 36px;
        border: 3px solid $color-bg-offset; // Use offset instead of border color for track
        border-top-color: $color-primary;
        border-radius: 50%;
        animation: spin 0.8s linear infinite;
    }

    @keyframes spin {
        to { transform: rotate(360deg); }
    }


</style>
